import { SkillInternal } from '@discretize/gw2-ui-new';
import {
  Skills,
  TextDivider,
  Weapons,
} from '@discretize/react-discretize-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import Conditional from '../components/Conditional';
import Embed from '../components/Embed';
import EmbossedCard from '../components/EmbossedCard';
import EmbossedTabs from '../components/EmbossedTabs';
import { GifPlayer } from '../components/GifPlayer';
import Label from '../components/Label';
import Message from '../components/Message';
import ProfessionVideo from '../components/ProfessionVideo';
import Rating from '../components/Rating';
import SimpleList from '../components/SimpleList';
import SimpleListItem from '../components/SimpleListItem';
import CharacterWithAr from '../components/character/CharacterWithAR';
import Traits from '../components/character/Traits';
import Boss from '../components/cm-guides/Boss';
import IdealRotation from '../components/cm-guides/IdealRotation';
import CMInformation from '../components/cm-guides/Information';
import InformationBlock from '../components/cm-guides/InformationBlock';
import Phase from '../components/cm-guides/Phase';
import {
  Attribute,
  Augmentation,
  Boon,
  CommonEffect,
  Condition,
  ControlEffect,
  Item,
  MistlockInstability,
  Skill,
  SpecialActionKey,
  Specialization,
  Trait,
  Uncategorized,
} from '../components/gw2components';
import Achievement from '../components/gw2components/Achievement';
import BuildLink from '../components/gw2components/BuildLink';
import Composition from '../components/gw2components/Composition';
import Legends from '../components/gw2components/Legends';
import Link from '../components/navigation/Link';
import { isBeginner } from '../state';

const stringToNumberArray = (prop, arr) =>
  arr ? { [prop]: arr.split(',')?.map((t) => parseInt(t, 10)) } : null;

const stringToStringArray = (prop, arr) =>
  arr ? { [prop]: arr.split(',')?.map((t) => t.trim()) } : null;

const stringToNumber = (prop, str) =>
  str ? { [prop]: parseInt(str, 10) } : null;

const intProp = (key) => ({
  [key]: (value) => ({
    key,
    value: value !== undefined && value !== null && parseInt(value, 10),
  }),
});

export const markdownComponents = {
  p: ({ children }) => {
    const childArray = React.Children.toArray(children);

    if (
      childArray &&
      childArray.length > 0 &&
      (React.Children.count(children) > 1 ||
        typeof childArray[0] === 'string' ||
        childArray[0].type === 'strong' ||
        childArray[0].type === 'em')
    ) {
      return React.createElement(
        Typography,
        { variant: 'body2', paragraph: true, component: 'div' },
        children,
      );
    }

    return children;
  },
  a: ({ href, ...rest }) =>
    React.createElement(Link, { to: href, enableIcon: true, ...rest }),

  h1: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'h4', gutterBottom: true },
      children,
    ),

  h2: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'h5', gutterBottom: true },
      children,
    ),

  h3: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'h6', gutterBottom: true },
      children,
    ),
  h4: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'subtitle1', component: 'h6', gutterBottom: true },
      children,
    ),
  h5: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'subtitle1', component: 'h6', gutterBottom: true },
      children,
    ),
  h6: ({ children }) =>
    React.createElement(
      Typography,
      { variant: 'subtitle1', component: 'h6', gutterBottom: true },
      children,
    ),
  hr: Divider,
  table: ({ children }) => <Table padding="normal">{children}</Table>,
  thead: ({ children }) => {
    // finds out, if it should render a table head.
    const hasHead =
      React.Children.map(
        children.props?.children,
        (child) => child.props.children,
      )?.filter(Boolean).length > 0;

    return hasHead ? (
      <TableHead>
        <TableRow>
          {React.Children.map(children.props.children, (headElement) => {
            const tableHead = React.Children.map(
              headElement,
              (child2) => child2,
            );

            if (tableHead) return tableHead;
            return <TableCell />;
          })}
        </TableRow>
      </TableHead>
    ) : null;
  },
  tbody: TableBody,
  tr: TableRow,
  th: ({ children }) =>
    React.createElement(TableCell, { component: 'th' }, children),
  td: ({ children }) => <TableCell component="td">{children}</TableCell>,
  ul: ({ children }) =>
    React.createElement(SimpleList, { dense: true }, children),
  ol: ({ children }) =>
    React.createElement(SimpleList, { ordered: true }, children),
  li: SimpleListItem,
};

export const customComponents = {
  br: () => <br />,
  Card: ({ title, specialization, children }) => (
    <EmbossedCard title={title} specialization={specialization}>
      {children}
    </EmbossedCard>
  ),
  Tabs: ({ children, ...rest }) =>
    React.createElement(
      EmbossedTabs,
      {
        ...rest,
        panes: React.Children.map(
          children,
          ({ props: { title, specialization, children: content } }) => ({
            title,
            specialization,
            content,
          }),
        ),
      },
      null,
    ),
  Tab: () => null,
  Rating: ({ value }) => <Rating value={value} />,
  Label: ({ children }) => <Label>{children}</Label>,
  Video: Embed,
  ProfessionVideo,
  GifPlayer,
  Achievement,
  BuildLink,
  Composition,
  Warning: Message,
  Information: ({ children }) => <Message icon={InfoIcon}>{children}</Message>,
  CMInformation,
  Legends,
  Boss: ({ children, ...rest }) => {
    const numerizedProps = { ...rest };
    Object.keys(rest).forEach((prop) => {
      if (prop.includes('Id')) {
        numerizedProps[prop] = parseInt(rest[prop], 10);
      }
    });
    const props = {
      ...numerizedProps,
    };
    return <Boss {...props}>{children}</Boss>;
  },
  Item,
  Skill,
  SkillInternal,
  Trait,
  SpecialActionKey,
  Uncategorized,
  Boon,
  Condition,
  Control: ControlEffect,
  Effect: CommonEffect,
  Instability: MistlockInstability,
  Attribute,
  Augmentation,
  Specialization,
  Grid: ({ children, ...rest }) =>
    React.createElement(
      'div',
      { style: { paddingTop: 8, paddingBottom: 8 } },
      React.createElement(
        Grid,
        { container: true, spacing: 2, ...rest },
        children,
      ),
    ),

  GridItem: ({ children, ...rest }) => {
    const props = Object.assign(
      {},
      ...Object.keys(rest).map((k) => ({
        [k]: {
          ...intProp('xs'),
          ...intProp('sm'),
          ...intProp('md'),
          ...intProp('lg'),
          ...intProp('xl'),
        }[k](rest[k]).value,
      })),
    );
    return React.createElement(
      Grid,
      { item: true, xs: 12, sm: 6, ...props },
      children,
    );
  },
  Divider: TextDivider,
  Weapons,
  Traits,
  Skills,
  Phase,
  InformationBlock,
  IdealRotation: ({ children, title }) => (
    <IdealRotation title={title}>{children}</IdealRotation>
  ),
  Character: (props) => null,
  CharacterWithAr: ({ children, ...rest }) => {
    const title =
      typeof window === 'undefined' ? '' : window?.location.pathname;

    const beginnerMode = useSelector(isBeginner(title));

    // Children of this element are `Character` components.
    // Grab the props of these character components and insert them as prop for CharacterWithAr
    const characters = React.Children.toArray(children)
      .filter((char) => typeof char === 'object')
      .filter(
        (character) =>
          (typeof character.props.beginner === 'undefined' &&
            typeof character.props.advanced === 'undefined') ||
          character.props.beginner === beginnerMode ||
          character.props.advanced === !beginnerMode,
      )
      .map((character) => ({
        title: character.props.title,
        gear: JSON.parse(character.props.gear),
        children: character.props.children,
      }));
    return <CharacterWithAr {...rest} characters={characters} />;
  },
  Beginner: ({ children }) => <Conditional beginner>{children}</Conditional>,
  Advanced: ({ children }) => <Conditional>{children}</Conditional>,
  Accordion: ({ title, children }) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {title}
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  ),
};

export default {
  ...markdownComponents,
  ...customComponents,
};
